import { supabase } from './client';
import type {
  Profile,
  Inquiry,
  InquiryFormData,
  Project,
  ProjectUpdate,
  Deliverable,
  BlogPost,
  Payment,
  InquiryStatus,
  ProjectStatus,
} from '../../types';

// Profile queries
export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }

  return data as Profile;
}

export async function updateProfile(
  userId: string,
  updates: Partial<Profile>
): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating profile:', error);
    return null;
  }

  return data as Profile;
}

export async function getAllClients(): Promise<Profile[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('role', 'client')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching clients:', error);
    return [];
  }

  return data as Profile[];
}

// Inquiry queries
export async function createInquiry(inquiry: InquiryFormData): Promise<Inquiry | null> {
  const { data, error } = await supabase
    .from('inquiries')
    .insert({
      ...inquiry,
      status: 'new' as const,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating inquiry:', error);
    return null;
  }

  // Send email notification via Edge Function
  try {
    await supabase.functions.invoke('send-inquiry-notification', {
      body: {
        type: 'INSERT',
        table: 'inquiries',
        record: data,
        schema: 'public',
      },
    });
  } catch (notifyError) {
    // Log but don't fail the submission if notification fails
    console.error('Error sending notification:', notifyError);
  }

  return data as Inquiry;
}

export async function getInquiries(): Promise<Inquiry[]> {
  const { data, error } = await supabase
    .from('inquiries')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching inquiries:', error);
    return [];
  }

  return data as Inquiry[];
}

export async function updateInquiryStatus(
  id: string,
  status: InquiryStatus
): Promise<boolean> {
  const { error } = await supabase
    .from('inquiries')
    .update({ status })
    .eq('id', id);

  if (error) {
    console.error('Error updating inquiry status:', error);
    return false;
  }

  return true;
}

// Project queries
export async function getProjects(clientId?: string): Promise<Project[]> {
  let query = supabase
    .from('projects')
    .select('*, client:profiles(*)')
    .order('updated_at', { ascending: false });

  if (clientId) {
    query = query.eq('client_id', clientId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching projects:', error);
    return [];
  }

  return data as Project[];
}

export async function getProject(id: string): Promise<Project | null> {
  const { data, error } = await supabase
    .from('projects')
    .select('*, client:profiles(*)')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching project:', error);
    return null;
  }

  return data as Project;
}

export async function createProject(
  project: Omit<Project, 'id' | 'created_at' | 'updated_at'>
): Promise<Project | null> {
  const { data, error } = await supabase
    .from('projects')
    .insert(project)
    .select()
    .single();

  if (error) {
    console.error('Error creating project:', error);
    return null;
  }

  return data as Project;
}

export async function updateProject(
  id: string,
  updates: Partial<Project>
): Promise<Project | null> {
  const { data, error } = await supabase
    .from('projects')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating project:', error);
    return null;
  }

  return data as Project;
}

export async function updateProjectStatus(
  id: string,
  status: ProjectStatus
): Promise<boolean> {
  const { error } = await supabase
    .from('projects')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    console.error('Error updating project status:', error);
    return false;
  }

  return true;
}

// Project Updates queries
export async function getProjectUpdates(
  projectId: string,
  isAdmin: boolean = false
): Promise<ProjectUpdate[]> {
  let query = supabase
    .from('project_updates')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (!isAdmin) {
    query = query.eq('is_visible_to_client', true);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching project updates:', error);
    return [];
  }

  return data as ProjectUpdate[];
}

export async function createProjectUpdate(
  update: Omit<ProjectUpdate, 'id' | 'created_at'>
): Promise<ProjectUpdate | null> {
  const { data, error } = await supabase
    .from('project_updates')
    .insert(update)
    .select()
    .single();

  if (error) {
    console.error('Error creating project update:', error);
    return null;
  }

  return data as ProjectUpdate;
}

// Deliverables queries
export async function getDeliverables(projectId: string): Promise<Deliverable[]> {
  const { data, error } = await supabase
    .from('deliverables')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching deliverables:', error);
    return [];
  }

  return data as Deliverable[];
}

export async function createDeliverable(
  deliverable: Omit<Deliverable, 'id'>
): Promise<Deliverable | null> {
  const { data, error } = await supabase
    .from('deliverables')
    .insert(deliverable)
    .select()
    .single();

  if (error) {
    console.error('Error creating deliverable:', error);
    return null;
  }

  return data as Deliverable;
}

export async function updateDeliverable(
  id: string,
  updates: Partial<Deliverable>
): Promise<boolean> {
  const { error } = await supabase
    .from('deliverables')
    .update(updates)
    .eq('id', id);

  if (error) {
    console.error('Error updating deliverable:', error);
    return false;
  }

  return true;
}

// Blog posts queries
export async function getPosts(publishedOnly: boolean = true): Promise<BlogPost[]> {
  let query = supabase
    .from('posts')
    .select('*')
    .order('published_at', { ascending: false });

  if (publishedOnly) {
    query = query.eq('is_published', true);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching posts:', error);
    return [];
  }

  return data as BlogPost[];
}

export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) {
    console.error('Error fetching post:', error);
    return null;
  }

  return data as BlogPost;
}

export async function createPost(
  post: Omit<BlogPost, 'id' | 'created_at'>
): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from('posts')
    .insert(post)
    .select()
    .single();

  if (error) {
    console.error('Error creating post:', error);
    return null;
  }

  return data as BlogPost;
}

export async function updatePost(
  id: string,
  updates: Partial<BlogPost>
): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from('posts')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating post:', error);
    return null;
  }

  return data as BlogPost;
}

export async function deletePost(id: string): Promise<boolean> {
  const { error } = await supabase.from('posts').delete().eq('id', id);

  if (error) {
    console.error('Error deleting post:', error);
    return false;
  }

  return true;
}

// Payments queries
export async function getPayments(clientId?: string): Promise<Payment[]> {
  let query = supabase
    .from('payments')
    .select('*, client:profiles(*)')
    .order('created_at', { ascending: false });

  if (clientId) {
    query = query.eq('client_id', clientId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching payments:', error);
    return [];
  }

  return data as Payment[];
}

export async function createPayment(
  payment: Omit<Payment, 'id' | 'created_at'>
): Promise<Payment | null> {
  const { data, error } = await supabase
    .from('payments')
    .insert(payment)
    .select()
    .single();

  if (error) {
    console.error('Error creating payment:', error);
    return null;
  }

  return data as Payment;
}

export async function updatePaymentStatus(
  id: string,
  status: Payment['status'],
  stripePaymentId?: string
): Promise<boolean> {
  const updates: Record<string, unknown> = { status };
  if (stripePaymentId) {
    updates.stripe_payment_id = stripePaymentId;
  }

  const { error } = await supabase
    .from('payments')
    .update(updates)
    .eq('id', id);

  if (error) {
    console.error('Error updating payment status:', error);
    return false;
  }

  return true;
}

// Case studies queries (for public projects page)
export async function getCaseStudies() {
  const { data, error } = await supabase
    .from('case_studies')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching case studies:', error);
    return [];
  }

  return data;
}

export async function getCaseStudiesByFilter(
  industry?: string,
  serviceType?: string
) {
  let query = supabase
    .from('case_studies')
    .select('*')
    .order('created_at', { ascending: false });

  if (industry && industry !== 'all') {
    query = query.eq('industry', industry);
  }

  if (serviceType && serviceType !== 'all') {
    query = query.eq('service_type', serviceType);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching case studies:', error);
    return [];
  }

  return data;
}
